/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Text, Table, theme, Tooltip, FlexContainer } from "@nordcloud/gnui";
import {
  CloudWasteCategoryKey,
  CloudWasteSettingKey,
} from "~/generated/graphql";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { formatMoney } from "~/tools";

const StyledBadge = styled("div")<{ color: string }>`
  margin: ${theme.spacing.spacing01} 0;
  font-size: ${theme.fontSizes.sm};
  background: ${({ color }) => color};
  color: ${theme.color.text.text04};
  margin-left: ${theme.spacing.spacing02};
  padding: 0 ${theme.spacing.spacing02};
  border-radius: ${theme.spacing.spacing01};
  min-width: ${theme.spacing.spacing05};
  min-height: ${theme.spacing.spacing05};
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const CategoryLabels = {
  [CloudWasteCategoryKey.Idle]: "Idle Resources",
  [CloudWasteCategoryKey.Orphaned]: "Orphan Resources",
  [CloudWasteCategoryKey.OverProvisioned]: "Overprovisioned Resources",
  AWSRI: "AWS Reservations And Commitments",
  AzureRI: "Azure Reservations",
  [CloudWasteCategoryKey.Turbonomic]: "Turbonomic Actions",
} as const;

type Props = {
  color: string;
  category?: CloudWasteCategoryKey | "AWSRI" | "AzureRI";
  cloudWaste: string;
  cloudWasteYearly: string;
  estateRecords?: number;
  policies: CloudWasteSettingKey[];
  uri?: string;
  isVisible?: boolean;
};

export function CloudWasteWidgetCell({
  color,
  category,
  cloudWaste,
  cloudWasteYearly,
  estateRecords = 0,
  policies,
  isVisible,
  uri = `${ROUTES.estateRecords.index}?page=0&waste=true${policies
    .map(policyMapper)
    .join("")}`,
}: Props) {
  const { currency } = useCurrency();
  const isAWSRICaregory = category === "AWSRI";
  const isAzureRICategory = category === "AzureRI";
  const isRICategory = isAWSRICaregory || isAzureRICategory;

  return (
    <When condition={isVisible}>
      <Table.tr>
        <Table.td>
          <FlexContainer direction="column" alignItems="flex-start">
            <FlexContainer
              css={{ position: "relative", fontSize: theme.fontSizes.md }}
            >
              <Link to={uri}>{category && CategoryLabels[category]}</Link>

              <Tooltip
                caption={`${estateRecords} Estate Records`}
                minWidth="6rem"
              >
                <When condition={!isRICategory}>
                  <StyledBadge color={color}>{estateRecords}</StyledBadge>
                </When>
              </Tooltip>
            </FlexContainer>
          </FlexContainer>
        </Table.td>
        <Table.td>
          <FlexContainer direction="column" alignItems="flex-end">
            <Text tag="span" color={theme.color.text.success}>
              {formatMoney(cloudWaste, currency)}
            </Text>
          </FlexContainer>
        </Table.td>
        <Table.td>
          <FlexContainer direction="column" alignItems="flex-end">
            <Text tag="span">{formatMoney(cloudWasteYearly, currency)}</Text>
          </FlexContainer>
        </Table.td>
      </Table.tr>
    </When>
  );
}

const policyMapper = (policy: string, index: number) =>
  `&wastePolicy[${index}]=${policy}`;
